/**
 * Unified input — merges keyboard, gamepad, and an on-screen nipplejs joystick
 * into a single analog vector + action buttons. Games read `input.state` each
 * frame instead of caring about the device.
 */

import nipplejs, { type JoystickManager } from 'nipplejs';

export interface InputState {
  /** Movement vector, each axis in [-1, 1]. */
  moveX: number;
  moveY: number;
  /** Named actions currently held. */
  actions: Set<string>;
  /** True while any touch joystick is active. */
  usingTouch: boolean;
}

export interface KeyMap {
  [code: string]: 'up' | 'down' | 'left' | 'right' | string;
}

const DEFAULT_KEYS: KeyMap = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  KeyW: 'up',
  KeyS: 'down',
  KeyA: 'left',
  KeyD: 'right',
  Space: 'primary',
  ShiftLeft: 'secondary',
  Enter: 'confirm',
  Escape: 'pause',
  KeyE: 'special',
};

export class InputManager {
  readonly state: InputState = { moveX: 0, moveY: 0, actions: new Set(), usingTouch: false };
  private keys: KeyMap;
  private held = new Set<string>();
  private joystick: JoystickManager | null = null;
  private joyVec = { x: 0, y: 0 };
  private buttonEls: { el: HTMLElement; action: string; onDown: (e: Event) => void; onUp: (e: Event) => void }[] = [];
  private actionListeners = new Set<(action: string, down: boolean) => void>();

  constructor(keys: Partial<KeyMap> = {}) {
    this.keys = { ...DEFAULT_KEYS, ...keys } as KeyMap;
  }

  attach(target: Window | HTMLElement = window) {
    target.addEventListener('keydown', this.onKey as EventListener);
    target.addEventListener('keyup', this.onKey as EventListener);
  }
  detach(target: Window | HTMLElement = window) {
    target.removeEventListener('keydown', this.onKey as EventListener);
    target.removeEventListener('keyup', this.onKey as EventListener);
    this.destroyJoystick();
    this.buttonEls.forEach(({ el, onDown, onUp }) => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      el.removeEventListener('pointerleave', onUp);
    });
    this.buttonEls = [];
    this.actionListeners.clear();
  }

  onAction(fn: (action: string, down: boolean) => void): () => void {
    this.actionListeners.add(fn);
    return () => this.actionListeners.delete(fn);
  }
  private emitAction(action: string, down: boolean) {
    for (const fn of this.actionListeners) fn(action, down);
  }

  private onKey = (e: KeyboardEvent) => {
    const mapped = this.keys[e.code];
    if (!mapped) return;
    if (e.type === 'keydown' && ['up', 'down', 'left', 'right', 'primary', 'pause'].includes(mapped)) {
      e.preventDefault();
    }
    const down = e.type === 'keydown';
    if (down && this.held.has(mapped)) return;
    if (down) this.held.add(mapped);
    else this.held.delete(mapped);
    this.recomputeMovement();
    if (!['up', 'down', 'left', 'right'].includes(mapped)) {
      if (down) this.state.actions.add(mapped);
      else this.state.actions.delete(mapped);
      this.emitAction(mapped, down);
    }
  };

  private recomputeMovement() {
    if (this.state.usingTouch) return;
    let x = 0,
      y = 0;
    if (this.held.has('left')) x -= 1;
    if (this.held.has('right')) x += 1;
    if (this.held.has('up')) y -= 1;
    if (this.held.has('down')) y += 1;
    this.state.moveX = x;
    this.state.moveY = y;
  }

  /** Mount a nipplejs joystick into `zone`. */
  mountJoystick(zone: HTMLElement, opts: { mode?: 'static' | 'dynamic'; color?: string; size?: number } = {}) {
    this.destroyJoystick();
    this.joystick = nipplejs.create({
      zone,
      mode: opts.mode || 'static',
      position: { left: '50%', bottom: '30%' },
      color: opts.color || 'rgba(255,255,255,0.6)',
      size: opts.size || 110,
      restJoystick: true,
      fadeTime: 100,
    });
    this.joystick.on('start', () => {
      this.state.usingTouch = true;
    });
    this.joystick.on('move', (_e, data) => {
      if (!data.vector) return;
      this.joyVec.x = data.vector.x;
      this.joyVec.y = -data.vector.y; // nipple y is up-positive; game y is down-positive
      this.state.moveX = this.joyVec.x;
      this.state.moveY = this.joyVec.y;
      this.state.usingTouch = true;
    });
    this.joystick.on('end', () => {
      this.joyVec.x = 0;
      this.joyVec.y = 0;
      this.state.moveX = 0;
      this.state.moveY = 0;
    });
  }

  private destroyJoystick() {
    if (this.joystick) {
      this.joystick.destroy();
      this.joystick = null;
    }
  }

  /** Wire an on-screen button element to an action. */
  bindButton(el: HTMLElement, action: string) {
    const onDown = (e: Event) => {
      e.preventDefault();
      this.state.actions.add(action);
      this.emitAction(action, true);
      el.classList.add('is-pressed');
    };
    const onUp = (e: Event) => {
      e.preventDefault();
      this.state.actions.delete(action);
      this.emitAction(action, false);
      el.classList.remove('is-pressed');
    };
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    el.addEventListener('pointerleave', onUp);
    this.buttonEls.push({ el, action, onDown, onUp });
  }

  /** Poll connected gamepads and fold them into the state (call once per frame). */
  pollGamepad() {
    if (typeof navigator === 'undefined' || !navigator.getGamepads) return;
    const pads = navigator.getGamepads();
    for (const pad of pads) {
      if (!pad) continue;
      const [ax, ay] = [pad.axes[0] || 0, pad.axes[1] || 0];
      if (Math.abs(ax) > 0.15 || Math.abs(ay) > 0.15) {
        this.state.moveX = ax;
        this.state.moveY = ay;
      }
      if (pad.buttons[0]?.pressed) this.state.actions.add('primary');
      else if (!this.held.has('primary')) this.state.actions.delete('primary');
      if (pad.buttons[1]?.pressed) this.state.actions.add('secondary');
      if (pad.buttons[9]?.pressed) this.state.actions.add('pause');
    }
  }

  isDown(action: string) {
    return this.state.actions.has(action);
  }
}

/** True on touch-first devices — games use this to show on-screen controls. */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}
